import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Camera, Mail, Shield, RefreshCcw, KeyRound, AlertTriangle } from 'lucide-react';
import AdminSidebar from '../../components/AdminSidebar';
import api from '../../config/api';
import toast from 'react-hot-toast';
import { useAdminAuth } from '../../context/AdminAuthContext';

const AdminProfile = () => {
  const { refreshAdmin, setAdmin: updateAdminState } = useAdminAuth();
  const updateAdminStateRef = useRef(updateAdminState);
  
  // Keep ref updated with latest function
  useEffect(() => {
    updateAdminStateRef.current = updateAdminState;
  }, [updateAdminState]);

  const [profile, setProfile] = useState({
    firstName: '',
    lastName: '',
    email: '',
    avatarUrl: '',
    role: 'Admin',
    lastLogin: '',
  });
  const [avatarPreview, setAvatarPreview] = useState('');
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [resettingPassword, setResettingPassword] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [loading, setLoading] = useState(true);

  const fetchProfile = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/profile');
      if (response.data.success) {
        const data = response.data.admin;
        setProfile({
          firstName: data.firstName || '',
          lastName: data.lastName || '',
          email: data.email || '',
          avatarUrl: data.avatarUrl || '',
          role: data.role || 'Admin',
          lastLogin: data.lastLogin || data.updatedAt || '',
        });
        setAvatarPreview(data.avatarUrl || '');
        updateAdminStateRef.current(data);
      }
    } catch (error) {
      toast.error('Failed to load admin profile');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  const handleProfileChange = (field, value) => {
    setProfile((prev) => ({ ...prev, [field]: value }));
  };

  const handleAvatarChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select a valid image file');
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const base64 = reader.result;
      setAvatarPreview(base64);
      setProfile((prev) => ({ ...prev, avatarUrl: base64 }));
    };
    reader.readAsDataURL(file);
  };

  const handleSaveProfile = async () => {
    try {
      setSavingProfile(true);
      const payload = {
        firstName: profile.firstName.trim(),
        lastName: profile.lastName.trim(),
        email: profile.email.trim(),
        avatarUrl: profile.avatarUrl,
      };
      const response = await api.put('/admin/profile', payload);
      if (response.data.success) {
        toast.success('Profile updated successfully');
        updateAdminState(response.data.admin);
        refreshAdmin();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update profile');
    } finally {
      setSavingProfile(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast.error('New password and confirmation do not match');
      return;
    }

    try {
      setSavingPassword(true);
      const response = await api.put('/admin/profile/password', {
        currentPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword,
      });
      if (response.data.success) {
        toast.success('Password updated successfully');
        setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update password');
    } finally {
      setSavingPassword(false);
    }
  };

  const handleForgotPassword = async () => {
    try {
      setResettingPassword(true);
      const response = await api.post('/admin/profile/reset-password');
      if (response.data.success) {
        toast.success(response.data.message);
      }
    } catch (error) {
      toast.error('Failed to initiate password reset');
    } finally {
      setResettingPassword(false);
    }
  };

  const formatDateTime = (value) => {
    if (!value) return 'Not available';
    try {
      return new Date(value).toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return value;
    }
  };

  if (loading) {
    return (
      <AdminSidebar>
        <div className="flex items-center justify-center py-16">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
        </div>
      </AdminSidebar>
    );
  }

  return (
    <AdminSidebar>
      <div className="space-y-6">
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-r from-purple-600 via-pink-500 to-purple-600 p-6 md:p-8 text-white shadow-xl">
          <div className="absolute inset-0 opacity-20">
            <div className="absolute -top-10 right-0 w-64 h-64 bg-white rounded-full blur-3xl"></div>
          </div>
          <div className="relative z-10 flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
            <div className="flex items-center gap-4">
              <div className="relative">
                {avatarPreview ? (
                  <img
                    src={avatarPreview}
                    alt="Avatar"
                    className="w-24 h-24 rounded-full object-cover border-4 border-white/30 shadow-2xl"
                  />
                ) : (
                  <div className="w-24 h-24 rounded-full bg-white/20 flex items-center justify-center text-white text-3xl font-bold border-4 border-white/30 shadow-2xl">
                    {(profile.firstName?.[0] || 'A').toUpperCase()}
                  </div>
                )}
                <label
                  htmlFor="avatar-upload"
                  className="absolute bottom-0 right-0 w-9 h-9 rounded-full bg-white text-purple-600 shadow-lg flex items-center justify-center cursor-pointer hover:scale-105 transition-transform"
                >
                  <Camera className="h-4 w-4" />
                </label>
                <input
                  id="avatar-upload"
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleAvatarChange}
                />
              </div>
              <div>
                <p className="text-sm uppercase tracking-[0.3em] text-white/70">Admin Account</p>
                <h1 className="text-3xl font-bold leading-tight">
                  {profile.firstName} {profile.lastName}
                </h1>
                <p className="text-white/80 flex items-center gap-2 mt-2">
                  <Mail className="h-4 w-4" />
                  {profile.email}
                </p>
              </div>
            </div>
            <div className="flex flex-wrap gap-3">
              <button
                onClick={fetchProfile}
                disabled={savingProfile}
                className="inline-flex items-center justify-center px-5 py-3 rounded-lg bg-white/10 backdrop-blur hover:bg-white/20 transition-colors"
              >
                <RefreshCcw className="h-4 w-4 mr-2" />
                Refresh Data
              </button>
              <button
                onClick={handleSaveProfile}
                disabled={savingProfile}
                className="inline-flex items-center justify-center px-5 py-3 rounded-lg bg-white text-purple-700 font-semibold hover:bg-purple-50 disabled:opacity-60 transition-all shadow-lg"
              >
                {savingProfile ? (
                  <>
                    <RefreshCcw className="h-4 w-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Shield className="h-4 w-4 mr-2" />
                    Save Profile
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {[
            { label: 'Role', value: profile.role || 'Admin' },
            { label: 'Secure Email', value: profile.email },
            { label: 'Last Sync', value: formatDateTime(profile.lastLogin) },
          ].map((item) => (
            <div
              key={item.label}
              className="rounded-2xl border border-purple-100 bg-white p-4 shadow-sm"
            >
              <p className="text-xs uppercase tracking-[0.3em] text-gray-400">{item.label}</p>
              <p className="mt-2 text-lg font-semibold text-gray-900 break-words">{item.value}</p>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-2xl shadow-lg border border-purple-100 p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Profile Details</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
                <input
                  type="text"
                  value={profile.firstName}
                  onChange={(e) => handleProfileChange('firstName', e.target.value)}
                  className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-purple-500 focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
                <input
                  type="text"
                  value={profile.lastName}
                  onChange={(e) => handleProfileChange('lastName', e.target.value)}
                  className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-purple-500 focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  value={profile.email}
                  onChange={(e) => handleProfileChange('email', e.target.value)}
                  className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-purple-500 focus:outline-none"
                />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-lg border border-purple-100 p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Change Password</h2>
            <form className="space-y-4" onSubmit={handlePasswordChange}>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Current Password
                </label>
                <input
                  type="password"
                  value={passwordForm.currentPassword}
                  onChange={(e) =>
                    setPasswordForm((prev) => ({ ...prev, currentPassword: e.target.value }))
                  }
                  className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-purple-500 focus:outline-none"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">New Password</label>
                <input
                  type="password"
                  value={passwordForm.newPassword}
                  onChange={(e) =>
                    setPasswordForm((prev) => ({ ...prev, newPassword: e.target.value }))
                  }
                  className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-purple-500 focus:outline-none"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Confirm Password
                </label>
                <input
                  type="password"
                  value={passwordForm.confirmPassword}
                  onChange={(e) =>
                    setPasswordForm((prev) => ({ ...prev, confirmPassword: e.target.value }))
                  }
                  className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-purple-500 focus:outline-none"
                  required
                />
              </div>
              <button
                type="submit"
                disabled={savingPassword}
                className="w-full inline-flex items-center justify-center px-4 py-2 rounded-lg bg-gradient-to-r from-purple-600 to-pink-600 text-white font-semibold hover:from-purple-700 hover:to-pink-700 disabled:opacity-60"
              >
                {savingPassword ? (
                  <>
                    <RefreshCcw className="h-4 w-4 mr-2 animate-spin" />
                    Updating...
                  </>
                ) : (
                  <>
                    <KeyRound className="h-4 w-4 mr-2" />
                    Update Password
                  </>
                )}
              </button>
            </form>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-lg border border-purple-100 p-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center text-red-500">
                <AlertTriangle className="h-7 w-7" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900">Forgot Password?</h3>
                <p className="text-gray-600">
                  Send yourself a secure password reset link. This will invalidate your current
                  password.
                </p>
              </div>
            </div>
            <button
              onClick={handleForgotPassword}
              disabled={resettingPassword}
              className="inline-flex items-center justify-center px-5 py-3 rounded-lg border border-red-200 text-red-600 font-semibold hover:bg-red-50 disabled:opacity-60"
            >
              {resettingPassword ? (
                <>
                  <RefreshCcw className="h-4 w-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Mail className="h-4 w-4 mr-2" />
                  Send Reset Link
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </AdminSidebar>
  );
};

export default AdminProfile;

